import { useCallback, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import { useRemoteFetchPlaceScheduleMutation } from "../../../hooks/Remote/Schedule/useRemoteFetchPlaceScheduleMutation";
import {
  buildColleagueWorkplaceSeatStatus,
  colleagueFloorScheduleQueryRange,
  occupancyBookingsForDesk,
  shouldOpenColleagueOccupancyTable
} from "./colleague-workplace-floor.functions";

/** Logged-in user, used when the occupied desk is theirs and colleagues are not listed. */
type CurrentUser = Pick<ConnectedBookingData, "userId" | "firstName" | "lastName" | "email">;

type Args = {
  date: string;
  currentFloor?: FloorAvailability;
  currentUser: CurrentUser;
  refetchFloor: () => void;
};

function matchOccupancySchedule(
  schedules: BookingScheduleInterface[],
  booking: ConnectedBookingData
): BookingScheduleInterface | undefined {
  if (booking.bookingId != null) {
    const byId = schedules.find(schedule => schedule.id === booking.bookingId);
    if (byId) {
      return byId;
    }
  }
  return schedules.find(
    schedule =>
      schedule.bookingInventoryId === booking.bookingInventoryId &&
      schedule.userBookedFor === booking.userId
  );
}

export function useColleagueFloorOccupancy({
  date,
  currentFloor,
  currentUser,
  refetchFloor
}: Args) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const fetchPlaceSchedule = useRemoteFetchPlaceScheduleMutation();
  const [occupancyInventoryId, setOccupancyInventoryId] = useState<number | undefined>();
  const [selectedSchedule, setSelectedSchedule] = useState<BookingScheduleInterface | undefined>();

  const seatStatus = useMemo(
    () =>
      currentFloor
        ? buildColleagueWorkplaceSeatStatus(currentFloor)
        : {
            availableList: [],
            occupiedList: [],
            disableList: [],
            restrictedList: [],
            userOccupiedList: [],
            partialList: []
          },
    [currentFloor]
  );

  const occupancyBookings = useMemo(() => {
    if (occupancyInventoryId == null) {
      return [];
    }
    return occupancyBookingsForDesk({
      inventoryId: occupancyInventoryId,
      connectedBookingData: currentFloor?.connectedBookingData ?? [],
      currentUser,
      isUserOccupied: new Set(seatStatus.userOccupiedList).has(occupancyInventoryId)
    });
  }, [
    currentFloor?.connectedBookingData,
    currentUser,
    occupancyInventoryId,
    seatStatus.userOccupiedList
  ]);

  const closeSchedule = useCallback(() => {
    setSelectedSchedule(undefined);
  }, []);

  const closeOccupancyTable = useCallback(() => {
    setOccupancyInventoryId(undefined);
  }, []);

  const openScheduleForBooking = useCallback(
    async (booking: ConnectedBookingData) => {
      const range = colleagueFloorScheduleQueryRange(date);
      if (range == null || booking.userId === "") {
        enqueueSnackbar(t("_colleagueFloor_openBookingError"), { variant: "error" });
        return;
      }

      try {
        const schedules = await fetchPlaceSchedule.mutateAsync({
          userIds: [booking.userId],
          startDate: range.startDate,
          endDate: range.endDate
        });
        const match = matchOccupancySchedule(schedules, booking);
        if (!match) {
          enqueueSnackbar(t("_colleagueFloor_openBookingError"), { variant: "error" });
          return;
        }
        setSelectedSchedule(match);
      } catch {
        enqueueSnackbar(t("_colleagueFloor_openBookingError"), { variant: "error" });
      }
    },
    [date, enqueueSnackbar, fetchPlaceSchedule, t]
  );

  const handleOccupancyInventoryId = useCallback(
    async (inventoryId: number) => {
      const bookings = occupancyBookingsForDesk({
        inventoryId,
        connectedBookingData: currentFloor?.connectedBookingData ?? [],
        currentUser,
        isUserOccupied: new Set(seatStatus.userOccupiedList).has(inventoryId)
      });

      if (shouldOpenColleagueOccupancyTable(bookings)) {
        setOccupancyInventoryId(inventoryId);
        return;
      }

      if (bookings.length === 1) {
        await openScheduleForBooking(bookings[0]);
        return;
      }

      enqueueSnackbar(t("_colleagueFloor_noDeskBookings"), { variant: "info" });
    },
    [
      currentFloor?.connectedBookingData,
      currentUser,
      enqueueSnackbar,
      openScheduleForBooking,
      seatStatus,
      t
    ]
  );

  const refetchAfterScheduleChange = useCallback(() => {
    refetchFloor();
  }, [refetchFloor]);

  const blocksParentClose = occupancyInventoryId != null || selectedSchedule != null;

  return {
    occupancyInventoryId,
    occupancyBookings,
    occupancyTableOpen: occupancyInventoryId != null,
    selectedSchedule,
    isOpeningBooking: fetchPlaceSchedule.isLoading,
    blocksParentClose,
    handleOccupancyInventoryId,
    openScheduleForBooking,
    closeOccupancyTable,
    closeSchedule,
    refetchAfterScheduleChange
  };
}
